import type { UserWordSummary } from '@/types/user'

export function groupWordsByLetter(words: UserWordSummary[]): Map<string, UserWordSummary[]> {
  const groups = new Map<string, UserWordSummary[]>()
  const sorted = [...words].sort((a, b) =>
    a.word.localeCompare(b.word, undefined, { sensitivity: 'base' }),
  )
  for (const w of sorted) {
    const letter = w.word.charAt(0).toUpperCase()
    const key = /[A-ZА-ЯЁ]/i.test(letter) ? letter : '#'
    const list = groups.get(key) ?? []
    list.push(w)
    groups.set(key, list)
  }
  return new Map([...groups.entries()].sort(([a], [b]) => a.localeCompare(b)))
}

export function letterSectionId(letter: string) {
  return `vocab-letter-${letter}`
}

export function dateSectionId(label: string) {
  return `vocab-date-${label.replace(/\s+/g, '-').toLowerCase()}`
}

function startOfDay(date: Date): Date {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  return d
}

function formatDateGroupLabel(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return 'Unknown date'

  const today = startOfDay(new Date())
  const target = startOfDay(date)
  const diffDays = Math.round((today.getTime() - target.getTime()) / 86_400_000)

  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) {
    return date.toLocaleDateString('en', { weekday: 'long' })
  }
  return date.toLocaleDateString('en', { month: 'short', day: 'numeric', year: 'numeric' })
}

export function groupWordsByDateAdded(words: UserWordSummary[]): Map<string, UserWordSummary[]> {
  const groups = new Map<string, UserWordSummary[]>()
  const sorted = [...words].sort((a, b) => {
    const aTime = a.dateAdded ? new Date(a.dateAdded).getTime() : 0
    const bTime = b.dateAdded ? new Date(b.dateAdded).getTime() : 0
    return bTime - aTime
  })

  for (const word of sorted) {
    const key = word.dateAdded ? formatDateGroupLabel(word.dateAdded) : 'Unknown date'
    const list = groups.get(key) ?? []
    list.push(word)
    groups.set(key, list)
  }

  return groups
}
